import React from 'react'
import { DataNode } from 'antd/lib/tree'
import { Button, Input, Spin, Tree } from 'antd'
import styles from './SelectTaxonomy.module.scss'
import { ITaxonomyTreeItemSimple, taxonomyService } from '../../../services/TaxonomyService'

interface Props {
  businessId: string
  onSelect: (businessId: string, item: ITaxonomyTreeItemSimple) => void
}

export const SelectTaxonomy = ({ businessId, onSelect }: Props) => {
  const [loading, setLoading] = React.useState(true)
  const [items, setItems] = React.useState<ITaxonomyTreeItemSimple[]>([])
  const [nodes, setNodes] = React.useState<Node[]>([])
  const [filteredNodes, setFilteredNodes] = React.useState<Node[]>([])
  const [search, setSearch] = React.useState('')

  React.useEffect(() => {
    setLoading(true)
    setNodes([])
    taxonomyService.getSimpleTaxonomyTree(businessId)
      .then(setItems)
      .finally(() => setLoading(false))
  }, [businessId])

  React.useEffect(() => {
    setNodes(items.map(convertItemToNode))
  }, [items])

  React.useEffect(() => {
    setFilteredNodes(treeSearch(search, nodes))
  }, [search, nodes])

  const handleSelect = React.useCallback((node: Node) => {
    businessId && onSelect(businessId, node.item)
  }, [businessId, onSelect])

  return (
    <div className={styles.root}>
      {loading && (
        <div className={styles.loader}>
          <Spin size={'large'} />
        </div>
      )}
      {!loading && (
        <Input.Search
          allowClear
          className={'mb4'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      )}
      {filteredNodes.length > 0 && !loading && (
        <Tree<Node>
          treeData={filteredNodes}
          onSelect={(_, e) => handleSelect(e.node as unknown as Node)}
        />
      )}
    </div>
  )
}

interface Node extends DataNode {
  item: ITaxonomyTreeItemSimple
  children: Node[] | undefined
}

function convertItemToNode (item: ITaxonomyTreeItemSimple): Node {
  const isCategory = !!(item.children && item.children.length > 0)
  return {
    item,
    key: item.id,
    title: isCategory ? item.name : <Button type={'link'} className={styles.nodeButton}>{item.name}</Button>,
    selectable: !isCategory,
    isLeaf: !isCategory,
    className: styles.node,
    children: item.children?.map(convertItemToNode),
  }
}

function treeSearch(search: string, nodes: Node[]): Node[] {
  return nodes.filter(i => i.item.name.includes(search) || (i.children && treeSearch(search, i.children).length > 0))
}
